import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import {
    Button,
    CircularProgress,
    Grid,
    Stack,
    Typography,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    Chip,
    Tooltip,
    IconButton,
    DialogActions,
    Table,
    TableRow,
    TableHead,
    TableCell,
    TableBody,
    Box,
    Pagination,
} from '@mui/material';
import {
    SearchOutlined,
    Replay,
    Delete as DeleteIcon,
    ContentCopy,
    DataObject,
    Code,
    Refresh,
    Upload,
} from '@mui/icons-material';

import useDebouncer from 'src/common/useDebouncer';
import { useAppSelector } from 'src/redux-app-hooks';
import {
    clearUploadResult,
    deleteOffer,
    fetchOfferDetails,
    fetchOffers,
    retryOffer,
    uploadOfferFile,
} from './offer-processing.slice';
import { useDispatch } from 'react-redux';
import { NotificationSeverity, showSnackbar } from '../notifications/notifications.slice';
import { OfferDetail, OfferListItemDto, OfferProcessingStatus } from './offer-processing.dto';
import dayjs from 'dayjs';
import { saveJsonString, saveXmlString } from './offer-processing.utils';

const SEARCH_DEBOUNCE_DELAY_MS = 500;
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_PAGE_SIZE = 15;

export default function OffersListPage() {
    const [isLoading, setIsLoading] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams({
        page: DEFAULT_PAGE_NUMBER.toString(),
        perPage: DEFAULT_PAGE_SIZE.toString(),
        searchString: '',
    });
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const dispatch = useDispatch();
    const {
        isLoading: isUploading,
        uploadResult,
        isLoadingList,
        items,
        rowPending,
        errorMessage,
    } = useAppSelector((state) => state.offerProcessing);
    const { t } = useTranslation();
    const debounce = useDebouncer();
    const [detailsSpinner, setDetailsSpinner] = useState<Record<string, 'json' | 'xml' | null>>({});
    useEffect(() => {
        loadOffers();
    }, [searchParams]);
    type OfferRow = OfferListItemDto & Partial<OfferDetail>;
    type SpinnerKind = 'json' | 'xml' | null;

    const JSON_OK_STATUSES = [
        OfferProcessingStatus.SUCCEEDED_GAEB,
        OfferProcessingStatus.SUCCEEDED_GAEB_WITH_WARNINGS,
        OfferProcessingStatus.SUCCEEDED_JSON,
        OfferProcessingStatus.SUCCEEDED_JSON_WITH_WARNINGS,
    ];

    const XML_OK_STATUSES = [OfferProcessingStatus.SUCCEEDED_GAEB, OfferProcessingStatus.SUCCEEDED_GAEB_WITH_WARNINGS];

    const baseNameFor = (row: OfferRow) => (row.fileName ?? `${row.fileId}`).replace(/\.[^.]+$/, '');

    function makeDownloadHandler<T>(
        kind: Exclude<SpinnerKind, null>,
        extract: (enriched: OfferRow) => T | undefined,
        save: (content: T, filenameBase: string) => void,
        emptyMessage: string,
    ) {
        return async (row: OfferRow) => {
            try {
                setDetailsSpinner((s) => ({ ...s, [row.fileId]: kind }));
                const enriched = await ensureDetails(row);
                const content = extract(enriched);
                if (content == null) {
                    dispatch(showSnackbar({ message: emptyMessage, severity: NotificationSeverity.Error }));
                    return;
                }
                save(content, baseNameFor(enriched));
            } catch (e: any) {
                dispatch(showSnackbar({ message: e?.message ?? String(e), severity: NotificationSeverity.Error }));
            } finally {
                setDetailsSpinner((s) => ({ ...s, [row.fileId]: null }));
            }
        };
    }

    async function loadOffers() {
        setIsLoading(true);
        const page = Number(searchParams.get('page')) || DEFAULT_PAGE_NUMBER;
        const perPage = Number(searchParams.get('perPage')) || DEFAULT_PAGE_SIZE;
        const searchString = searchParams.get('searchString') || '';
        await dispatch(fetchOffers({ page, perPage, searchString }) as any);
        setIsLoading(false);
    }

    async function ensureDetails(row: OfferRow): Promise<OfferRow> {
        if (row?.extractedJson || row?.gaebXml) return row;
        const details = await (dispatch as any)(fetchOfferDetails(row.fileId)).unwrap();
        return { ...row, ...details };
    }

    const onDownloadJson = makeDownloadHandler(
        'json',
        (enriched) => enriched.extractedJson,
        (content, base) =>
            saveJsonString(typeof content === 'string' ? content : JSON.stringify(content), `${base}.json`),
        t('No JSON available for this file'),
    );

    const onDownloadXml = makeDownloadHandler<string>(
        'xml',
        (enriched) => (enriched.gaebXml ? enriched.gaebXml : undefined),
        (xml, base) => saveXmlString(xml, `${base}.xml`),
        t('No XML available for this file'),
    );

    const statusChip = (status: OfferProcessingStatus) => {
        switch (status) {
            case OfferProcessingStatus.SUCCEEDED_JSON:
            case OfferProcessingStatus.SUCCEEDED_JSON_WITH_WARNINGS:
                return <Chip label={t('Json Extracted')} color="success" size="small" />;
            case OfferProcessingStatus.SUCCEEDED_GAEB:
            case OfferProcessingStatus.SUCCEEDED_GAEB_WITH_WARNINGS:
                return <Chip label={t('Completed')} color="success" size="small" />;
            case OfferProcessingStatus.FAILED:
                return <Chip label={t(status)} color="error" size="small" />;
            case OfferProcessingStatus.PROCESSING:
            case OfferProcessingStatus.QUEUED:
                return <Chip label={t('Processing')} color="warning" size="small" />;
            default:
                return <Chip label={t(status)} color="warning" size="small" />;
        }
    };

    const onRetry = (id: string) => dispatch(retryOffer(id) as any);
    const onDelete = (id: string) => {
        if (window.confirm(t('Are you sure you want to delete this offer?'))) {
            dispatch(deleteOffer(id) as any);
        }
    };

    function changePage(page: number) {
        setSearchParams((prev) => {
            const paramsObject = Object.fromEntries(prev.entries()); // Spread operator doesn't work directly on URLSearchParams
            return { ...paramsObject, page: page.toString() };
        });
    }

    useEffect(() => {
        if (errorMessage) {
            dispatch(showSnackbar({ message: errorMessage, severity: NotificationSeverity.Error }));
        }
    }, [errorMessage, dispatch]);

    const openFilePicker = () => fileInputRef.current?.click();

    const onFileSelected: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        for (const file of e.target.files ?? []) {
            dispatch(uploadOfferFile(file) as any);
        }
        e.target.value = '';
    };

    const closeSuccessDialog = () => {
        dispatch(clearUploadResult());
        loadOffers();
    };

    const copyToClipboard = (value: string) => {
        navigator.clipboard?.writeText(value).catch(() => {});
    };

    const statusColor: 'default' | 'success' | 'warning' =
        uploadResult?.status === 'UPLOADED' ? 'success' : uploadResult?.status === 'DUPLICATE' ? 'warning' : 'default';

    const getValidationErrors = (errorMessage: string | null | undefined) => {
        if (!errorMessage) return '-';

        const errors = [];

        try {
            const messages = JSON.parse(errorMessage);
            if (messages) {
                for (const message of messages) {
                    if (message['message']) {
                        errors.push(
                            message['position_number']
                                ? `${message['position_number']} - ${message['message']}`
                                : message['message'],
                        );
                    } else {
                        errors.push(message.toString());
                    }
                }
            } else {
                errors.push(errorMessage);
            }
        } catch (e) {
            errors.push(errorMessage);
        }

        return errors.map((error) => <li key={error}>{error}</li>);
    };

    return (
        <Grid container direction="row" size={12} spacing={2}>
            <Grid size={12}>
                <Typography variant="h4">{t('Offers Processing')}</Typography>
            </Grid>

            <Grid size={12}>
                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                    <TextField
                        variant="outlined"
                        size="small"
                        sx={{ width: 350 }}
                        placeholder={t('Search...')}
                        slotProps={{ input: { startAdornment: <SearchOutlined /> } }}
                        onChange={(e) =>
                            debounce(
                                () =>
                                    setSearchParams({
                                        page: searchParams.get('page') || DEFAULT_PAGE_NUMBER.toString(),
                                        perPage: searchParams.get('perPage') || DEFAULT_PAGE_SIZE.toString(),
                                        searchString: e.target.value,
                                    }),
                                SEARCH_DEBOUNCE_DELAY_MS,
                            )
                        }
                    />

                    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                        <Button
                            variant="contained"
                            onClick={() => loadOffers()}
                            disabled={isLoading}
                            loading={isLoading}
                            startIcon={<Refresh />}
                        >
                            {t('Refresh Offer List')}
                        </Button>

                        <input
                            ref={fileInputRef}
                            hidden
                            type="file"
                            onChange={onFileSelected}
                            accept="application/pdf"
                            multiple={true}
                        />

                        <Button
                            variant="contained"
                            onClick={openFilePicker}
                            disabled={isUploading}
                            startIcon={isUploading ? <CircularProgress size={18} /> : <Upload />}
                        >
                            {isUploading ? t('Uploading...') : t('Upload New Offer')}
                        </Button>
                    </Stack>
                </Stack>
            </Grid>

            <Grid size={12}>
                {isLoading || isLoadingList ? (
                    <CircularProgress />
                ) : (
                    <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ width: '25%' }}>{t('File name')}</TableCell>
                                <TableCell style={{ width: '15%' }}>{t('Uploaded at')}</TableCell>
                                <TableCell style={{ width: '10%' }}>{t('Status')}</TableCell>
                                <TableCell style={{ width: '40%' }}>{t('Validation Errors')}</TableCell>
                                <TableCell style={{ width: '10%' }} align="right">
                                    {t('Actions')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.content.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {t('No offers found.')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                items.content.map((row) => {
                                    const rowBusy = rowPending[row.fileId] || {};
                                    const retryBusy = !!rowBusy.retry;
                                    const deleteBusy = !!rowBusy.delete;
                                    const detailsBusy = !!rowBusy.details;
                                    const spinner = detailsSpinner[row.fileId];
                                    const canRetry = row.status === OfferProcessingStatus.FAILED;
                                    const canDownloadJson = JSON_OK_STATUSES.includes(row.status);
                                    const canDownloadXml = XML_OK_STATUSES.includes(row.status);

                                    return (
                                        <TableRow key={row.fileId} hover>
                                            <TableCell>{row.fileName}</TableCell>
                                            <TableCell>{dayjs(row.createdAt).format('YYYY-MM-DD HH:mm:ss')}</TableCell>
                                            <TableCell>{statusChip(row.status)}</TableCell>
                                            <TableCell>{getValidationErrors(row.errorMessage)}</TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                                                    <Tooltip title={t('Retry')}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => onRetry(row.fileId)}
                                                                disabled={!canRetry || retryBusy}
                                                            >
                                                                {retryBusy ? (
                                                                    <CircularProgress size={18} />
                                                                ) : (
                                                                    <Replay fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>

                                                    <Tooltip title={t('Download JSON')}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => onDownloadJson(row)}
                                                                disabled={!canDownloadJson || detailsBusy}
                                                            >
                                                                {spinner === 'json' ? (
                                                                    <CircularProgress size={18} />
                                                                ) : (
                                                                    <DataObject fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>

                                                    <Tooltip title={t('Download XML')}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => onDownloadXml(row)}
                                                                disabled={!canDownloadXml || detailsBusy}
                                                            >
                                                                {spinner === 'xml' ? (
                                                                    <CircularProgress size={18} />
                                                                ) : (
                                                                    <Code fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>

                                                    <Tooltip title={t('Delete')}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => onDelete(row.fileId)}
                                                                disabled={deleteBusy}
                                                            >
                                                                {deleteBusy ? (
                                                                    <CircularProgress size={18} />
                                                                ) : (
                                                                    <DeleteIcon fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                )}
            </Grid>

            <Grid size={12} container sx={{ justifyContent: 'center' }}>
                {!!items?.content.length && (
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                            variant="outlined"
                            count={items.page.totalPages}
                            page={Number(searchParams.get('page') || 1)}
                            onChange={(_, page) => changePage(page)}
                        />
                    </Box>
                )}
            </Grid>

            {/* Success popup */}
            <Dialog open={Boolean(uploadResult)} onClose={closeSuccessDialog} fullWidth maxWidth="sm">
                <DialogTitle>{t('File uploaded')}</DialogTitle>
                <DialogContent dividers>
                    {uploadResult && (
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <Typography variant="subtitle2">{t('Status')}:</Typography>
                                <Chip label={uploadResult.status} color={statusColor} variant="outlined" />
                            </Stack>

                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <Typography variant="subtitle2" sx={{ mr: 1 }}>
                                    {t('URI')}:
                                </Typography>
                                <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1 }}>
                                    {uploadResult.uri}
                                </Typography>
                                <Tooltip title={t('Copy')}>
                                    <IconButton size="small" onClick={() => copyToClipboard(uploadResult.uri)}>
                                        <ContentCopy fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>

                            <Stack direction="row" spacing={1}>
                                <Typography variant="subtitle2">{t('File ID')}:</Typography>
                                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                    {uploadResult.fileId}
                                </Typography>
                            </Stack>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeSuccessDialog} variant="contained">
                        {t('OK')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
}
